/**
 * Redesigned Design-tab left nav. Three content groups — Build (the Build
 * Plan card with the SINGLE status chip), Design Docs, and Screens — plus a
 * pinned one-line footer. Pure presentation: all state arrives derived
 * (design-status.ts) and every interaction is a callback.
 */
import type { ReactElement, ReactNode } from 'react';
import { Check, FileCheck2 } from 'lucide-react';
import type { Screen, SectionMeta } from '@volley/crucible-ipc-types';
import type {
  BuildPlanChip,
  NavFooterLine,
  ScreensGenerateState,
  StatusTone,
} from './design-status';

const CHIP_TONE_CLASS: Record<StatusTone, string> = {
  gray: 'border-white/15 bg-white/[0.06] text-crucible-muted',
  blue: 'border-crucible-accent/40 bg-crucible-accent/15 text-crucible-accent-hover',
  amber: 'border-amber-300/30 bg-amber-300/10 text-amber-200',
  green: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-300',
};

const FOOTER_TONE_CLASS: Record<StatusTone, string> = {
  gray: 'text-crucible-muted',
  blue: 'text-crucible-accent-hover',
  amber: 'text-amber-200',
  green: 'text-crucible-muted',
};

function navItemClass(active: boolean): string {
  return `flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${
    active
      ? 'bg-crucible-accent/20 text-crucible-fg'
      : 'text-crucible-muted hover:bg-crucible-bg-elevated'
  }`;
}

function GroupHeading({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-crucible-muted">
      {children}
    </h2>
  );
}

/** The 6px amber "edited since last build" dot. */
function EditedDot(): ReactElement {
  return (
    <span
      title="Edited since the last build"
      data-testid="design-edited-dot"
      className="size-1.5 shrink-0 rounded-full bg-amber-300"
    />
  );
}

export interface DesignNavProps {
  /** Build Plan surface available (the host passed a buildPlan). */
  hasBuildPlan: boolean;
  buildPlanActive: boolean;
  chip: BuildPlanChip;
  footer: NavFooterLine;
  sections: SectionMeta[];
  activeFilename: string | null;
  /** Section/screen ids edited since the last build snapshot (amber dots). */
  changedSectionIds: string[];
  changedScreenIds: string[];
  screens: Screen[];
  activeScreenId: string | null;
  generate: ScreensGenerateState;
  genError: string | null;
  /** Optional Game-profile strip, rendered under the Build group. */
  profileSlot?: ReactNode;
  onSelectBuildPlan: () => void;
  onSelectSection: (filename: string) => void;
  onSelectScreen: (screenId: string) => void;
  onNewScreen: () => void;
  onGenerateScreens: () => void;
}

export function DesignNav({
  hasBuildPlan,
  buildPlanActive,
  chip,
  footer,
  sections,
  activeFilename,
  changedSectionIds,
  changedScreenIds,
  screens,
  activeScreenId,
  generate,
  genError,
  profileSlot,
  onSelectBuildPlan,
  onSelectSection,
  onSelectScreen,
  onNewScreen,
  onGenerateScreens,
}: DesignNavProps): ReactElement {
  const anyEdited = changedSectionIds.length > 0 || changedScreenIds.length > 0;

  return (
    <nav className="flex w-60 shrink-0 flex-col border-r border-crucible-border">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {hasBuildPlan && (
          <>
            <div className="mb-1.5">
              <GroupHeading>Build</GroupHeading>
            </div>
            <button
              type="button"
              onClick={onSelectBuildPlan}
              data-testid="design-build-plan-nav"
              title="The approval artifact that starts a build — separate from your design docs"
              className={`mb-2 flex w-full flex-col gap-1.5 rounded-lg border p-2.5 text-left ${
                buildPlanActive
                  ? 'border-crucible-accent/50 bg-crucible-accent/10'
                  : 'border-crucible-border bg-white/[0.02] hover:border-crucible-accent/40'
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-crucible-fg">
                <FileCheck2 size={14} className="text-crucible-accent-hover" aria-hidden="true" />
                Build Plan
              </span>
              <span
                data-testid="build-plan-chip"
                data-state={chip.state}
                className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${CHIP_TONE_CLASS[chip.tone]}`}
              >
                {chip.pulse && (
                  <span
                    className="size-1.5 animate-pulse rounded-full bg-current"
                    aria-hidden="true"
                  />
                )}
                {chip.label}
              </span>
            </button>
          </>
        )}

        {profileSlot}

        <div className="mb-1 mt-4 first:mt-0">
          <GroupHeading>Design Docs</GroupHeading>
        </div>
        <ul className="space-y-0.5">
          {sections.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelectSection(s.filename)}
                className={navItemClass(activeFilename === s.filename)}
                title={s.title}
              >
                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                {changedSectionIds.includes(s.id) && <EditedDot />}
              </button>
            </li>
          ))}
        </ul>

        <div className="mb-1 mt-4 flex items-center justify-between">
          <GroupHeading>Screens</GroupHeading>
          <button
            type="button"
            onClick={onGenerateScreens}
            disabled={generate.state === 'busy'}
            data-testid="design-generate-screens"
            data-state={generate.state}
            title={generate.title}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] hover:bg-crucible-bg-elevated disabled:opacity-60 ${
              generate.state === 'stale'
                ? 'border-crucible-accent/50 text-crucible-accent-hover'
                : 'border-crucible-border text-crucible-muted'
            }`}
          >
            {generate.state === 'stale' && (
              <span
                className="size-1 rounded-full bg-crucible-accent-hover"
                data-testid="design-generate-stale-dot"
                aria-hidden="true"
              />
            )}
            {generate.state === 'busy' && (
              <span className="size-1 animate-pulse rounded-full bg-amber-300" aria-hidden="true" />
            )}
            {generate.label}
          </button>
        </div>
        {genError && (
          <p className="mb-1 text-[10px] text-red-400" data-testid="design-generate-screens-error">
            {genError}
          </p>
        )}
        <ul className="space-y-0.5">
          {screens.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelectScreen(s.id)}
                className={navItemClass(activeScreenId === s.id)}
                title={s.name}
              >
                <span className="text-[10px] uppercase opacity-60">{s.surface}</span>
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {changedScreenIds.includes(s.id) && <EditedDot />}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={onNewScreen}
              data-testid="design-new-screen"
              className="w-full rounded px-2 py-1 text-left text-sm text-crucible-accent-hover hover:bg-crucible-bg-elevated"
            >
              + New screen
            </button>
          </li>
        </ul>
        {anyEdited && (
          <p
            className="mt-3 flex items-center gap-1.5 px-2 text-[10px] text-crucible-muted"
            data-testid="design-edited-legend"
          >
            <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
            edited since last build
          </p>
        )}
      </div>

      {/* Pinned footer — exactly one quiet line per state. The send CTA lives
          in the right-rail Build card, never here. */}
      <div className="shrink-0 border-t border-crucible-border p-3">
        {footer.clickable ? (
          <button
            type="button"
            onClick={onSelectBuildPlan}
            data-testid="design-nav-footer"
            data-state={footer.kind}
            className={`flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-dashed px-2.5 py-1.5 text-left text-xs hover:bg-crucible-bg-elevated ${
              footer.tone === 'amber' ? 'border-amber-300/35' : 'border-white/20'
            } ${FOOTER_TONE_CLASS[footer.tone]}`}
          >
            <span className="min-w-0 truncate">{footer.text}</span>
            <span aria-hidden="true">›</span>
          </button>
        ) : (
          <div
            data-testid="design-nav-footer"
            data-state={footer.kind}
            className={`flex items-center gap-2 px-2.5 py-1.5 text-xs ${FOOTER_TONE_CLASS[footer.tone]}`}
          >
            {footer.kind === 'running' && (
              <span
                className="size-1.5 shrink-0 animate-pulse rounded-full bg-amber-300"
                aria-hidden="true"
              />
            )}
            {footer.kind === 'pending' && (
              <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
            )}
            {footer.kind === 'in-sync' && (
              <Check size={13} className="shrink-0 text-emerald-300" aria-hidden="true" />
            )}
            <span className="min-w-0 truncate">{footer.text}</span>
          </div>
        )}
      </div>
    </nav>
  );
}
